import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  ArrowRight, 
  Loader2, 
  Chrome,
  Compass,
  CheckCircle2,
  Zap,
  MousePointer2,
  Sparkles,
  Presentation,
  FileText,
  Check,
  MessageSquare
} from 'lucide-react';
import logo from '../assets/threadshare_logo.svg';
import { StorageUtils } from '../Utils/StorageUtils';

const MarketingPage = () => {
    const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState('raw'); // 'raw', 'slides', or 'docs'
  const [scrolled, setScrolled] = useState(false);

  // Extension URLs
  const chromeStoreUrl = "https://chromewebstore.google.com/detail/threadshare/mkdemnaiienmhffacabkpboljfcdmfel";
  const safariStoreUrl = "https://apps.apple.com/us/app/threadshare-for-safari/id6757684487?mt=12";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => setIsGenerating(false), 2000);
  };

  const handleGetStarted = () => {
    const userData = StorageUtils.getUserData();
    if (userData && userData.token) {
      // User is logged in, go to dashboard
      navigate('/dashboard');
    } else {
      // User is not logged in, go to login page
      navigate('/auth?type=login');
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans selection:bg-blue-100">
      <style>{`
        @keyframes ghostFadeIn {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
            filter: blur(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        .animate-ghost {
          animation: ghostFadeIn 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
      `}</style>
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${
        scrolled ? 'bg-white/70 backdrop-blur-md border-zinc-200 py-3' : 'bg-transparent border-transparent py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between relative">
          <div className="flex items-center gap-2.5 group cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src={logo} 
              alt="ThreadShare Logo" 
              className="w-12 h-12 transition-transform group-hover:scale-105"
            />
            <span className="font-extrabold text-3xl tracking-tighter text-zinc-900">ThreadShare</span>
          </div>
          
          <div className="hidden md:flex items-center gap-10 text-sm font-semibold absolute left-1/2 -translate-x-1/2">
            <a href="#how-it-works" className="text-zinc-900 no-underline hover:text-zinc-600 transition-colors">How it Works</a>
            <a href="#pricing" className="text-zinc-900 no-underline hover:text-zinc-600 transition-colors">Pricing</a>
          </div>

          <button 
            onClick={handleGetStarted}
            className="bg-zinc-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-zinc-800 transition-all active:scale-95 flex items-center gap-2"
          >
            <Zap className="w-4 h-4 text-blue-400 fill-blue-400" />
            Get Started
          </button>
        </div>
      </nav>

      <main className="pt-44 pb-20">
        
                {/* Hero Section */}
        <section className="max-w-5xl mx-auto text-center mb-24 px-6">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-full mb-8 animate-ghost" style={{ animationDelay: '0.2s' }}>
            <Zap className="w-3.5 h-3.5 text-blue-600 fill-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Supporting</span>
            <div className="flex gap-1.5 items-center ml-1 border-l border-blue-200 pl-2">
              <span className="text-xs font-semibold text-blue-800">ChatGPT • Claude • Gemini</span>
                                    </div>
                                </div>

          <h1 className="text-6xl md:text-8xl font-bold tracking-tighter mb-8 leading-[0.95] animate-ghost" style={{ animationDelay: '0.4s' }}>
            <span className="block whitespace-nowrap">Turn any AI chat into</span>
            <span className="block whitespace-nowrap text-zinc-300 italic">professional</span>
            <span className="block whitespace-nowrap text-zinc-300 italic">documents.</span>
          </h1>
          
          <p className="text-xl text-zinc-500 mb-10 max-w-3xl mx-auto leading-relaxed animate-ghost" style={{ animationDelay: '0.6s' }}>
            Threadshare converts your ChatGPT, Claude, and Gemini conversations into polished presentations and structured documents instantly via our browser extension.
          </p>

          <div className="flex flex-row items-center justify-center gap-4 animate-ghost" style={{ animationDelay: '0.8s' }}>
            <a
              href={chromeStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleGenerate}
              className="no-underline bg-zinc-900 text-white px-6 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-zinc-800 hover:shadow-xl hover:-translate-y-1 transition-all disabled:opacity-80"
            >
              {isGenerating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Chrome className="w-5 h-5" />
              )}
              Add to Chrome
            </a>
            
            <a
              href={safariStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline bg-blue-600 text-white px-6 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all shadow-blue-200/50"
            >
              <Compass className="w-5 h-5" />
              Add to Safari
            </a>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-zinc-400 font-medium animate-ghost" style={{ animationDelay: '1s' }}>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-blue-500" /> Auto-formatting</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-indigo-500" /> Presentation-ready</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-sky-500" /> Works instantly</span>
                                                </div>
        </section>

        {/* Live Preview Toggle Section */}
        <section id="how-it-works" className="py-32 px-6 overflow-hidden relative bg-blue-50">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-y-1/2" />
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-y-1/2" />

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-zinc-900">See it in action.</h2>
              <p className="text-lg text-zinc-500 max-w-2xl mx-auto">One click to bridge the gap between AI brainstorming and professional output.</p>
                                                    </div>

            <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-[2.5rem] p-4 md:p-8 shadow-xl shadow-blue-900/5">
              <div className="flex justify-center mb-8">
                <div className="bg-zinc-200/50 p-1.5 rounded-2xl flex gap-1 backdrop-blur-md">
                  <button 
                    onClick={() => setPreviewMode('raw')}
                    className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${previewMode === 'raw' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    Raw AI Chat
                                                        </button>
                  <button 
                    onClick={() => setPreviewMode('docs')}
                    className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${previewMode === 'docs' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    Document
                                                        </button>
                                                    </div>
                                                </div>

              <div className="bg-white border border-zinc-200 rounded-3xl aspect-video overflow-hidden transition-all duration-500 shadow-2xl shadow-zinc-200/50 relative">
                <div className="h-8 bg-zinc-100 border-b border-zinc-200 flex items-center px-4 gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                                            </div>
                  <div className="flex-1 bg-white mx-4 rounded-md h-5 text-[10px] flex items-center px-2 text-zinc-400">
                    {previewMode === 'raw' ? 'claude.ai/chat/japan-strategy' : 'threadshare.ai/p/japan-market-strategy'}
                                        </div>
                  {previewMode === 'raw' && (
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center animate-pulse cursor-pointer p-1">
                      <img src={logo} alt="ThreadShare" className="w-full h-full" />
                                                </div>
                  )}
                                        </div>
                                        
                <div className="h-[calc(100%-2rem)] overflow-y-auto">
                  {previewMode === 'raw' && (
                    <div className="p-8 flex flex-col gap-6 font-sans text-sm">
                      <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-lg bg-zinc-200 shrink-0 flex items-center justify-center text-zinc-600 font-bold text-xs">U</div>
                        <div className="flex-1">
                          <p className="text-zinc-800 font-medium">Outline a market entry strategy for Japan.</p>
                                                </div>
                                                </div>
                      <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 shrink-0 flex items-center justify-center">
                           <Sparkles className="w-4 h-4 text-blue-600" />
                                                    </div>
                        <div className="flex-1 space-y-3">
                          <p className="text-zinc-800">To enter the Japanese market successfully, you need to focus on:</p>
                          <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100 text-xs text-zinc-600">
                            1. Hyper-localization<br/>
                            2. Cultural relationship building<br/>
                            3. Quality assurance (Monozukuri)
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                  )}

                  {previewMode === 'docs' && (
                    <div className="h-full bg-white p-12 font-serif">
                       <div className="max-w-xl mx-auto space-y-6">
                          <h1 className="text-4xl font-bold border-b pb-4 text-zinc-900">Strategic Report: Japan</h1>
                          <div className="flex items-center gap-4 text-xs text-zinc-400 font-sans uppercase tracking-widest">
                            <span>January 2026</span>
                            <span>•</span>
                            <span>Internal Strategy</span>
                                    </div>
                          <p className="text-zinc-700 leading-relaxed text-lg">
                            Japan remains a high-potential market characterized by sophisticated consumer behavior and a strong preference for localized experiences. 
                          </p>
                          <h2 className="text-xl font-bold pt-4 text-zinc-900 font-sans tracking-tight">Core Strategic Pillars</h2>
                          <div className="space-y-4 font-sans">
                             <div className="flex gap-4 items-start border-l-2 border-blue-500 pl-4 py-1">
                                <div>
                                   <p className="font-bold text-zinc-900">Hyper-localization</p>
                                   <p className="text-sm text-zinc-500">Adapting messaging to local cultural nuances and linguistic patterns.</p>
                                </div>
                            </div>
                             <div className="flex gap-4 items-start border-l-2 border-indigo-500 pl-4 py-1">
                                <div>
                                   <p className="font-bold text-zinc-900">Monozukuri Mindset</p>
                                   <p className="text-sm text-zinc-500">Emphasizing craftsmanship and obsessive quality control standards.</p>
                                </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                  )}
                                </div>
                                            </div>
              
              <p className="text-center text-sm text-zinc-400 mt-6 flex items-center justify-center gap-2">
                <MousePointer2 className="w-3 h-3" />
                {previewMode === 'raw' ? 'The Threadshare button captures the thread' : 'Clean, professional document layout'}
                                                </p>
                                            </div>
                                        </div>
        </section>

        {/* Feature Grid */}
        <section className="max-w-7xl mx-auto px-6 mb-32 pt-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 hover:border-blue-200 transition-colors group">
              <div className="w-12 h-12 bg-white border border-zinc-200 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold tracking-tighter mb-2">Smart Formatting</h3>
              <p className="text-zinc-500 leading-snug">Automatically structure your AI conversations into clean, professional documents.</p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 hover:border-blue-200 transition-colors group">
              <div className="w-12 h-12 bg-white border border-zinc-200 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-6 h-6 text-indigo-500" />
              </div>
              <h4 className="text-xl font-bold tracking-tighter mb-2">Access Control</h4>
              <p className="text-sm text-zinc-500">Manage who can view your shared conversations with flexible privacy settings and viewer permissions.</p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 hover:border-blue-200 transition-colors group">
              <div className="w-12 h-12 bg-white border border-zinc-200 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MessageSquare className="w-6 h-6 text-blue-500" />
              </div>
              <h4 className="text-xl font-bold tracking-tighter mb-2">Inline Comments</h4>
              <p className="text-sm text-zinc-500">Collaborate with others by adding comments directly to your shared AI conversations and documents.</p>
            </div>
                    </div>
                </section>
                
        {/* Pricing Section */}
        <section id="pricing" className="bg-blue-50 py-32 px-6 overflow-hidden relative">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-y-1/2" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-y-1/2" />

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4">Simple, transparent pricing.</h2>
              <p className="text-lg text-zinc-500 max-w-2xl mx-auto">Get started for free or upgrade to Pro for advanced presentation tools and team features.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
              <div className="bg-white border border-zinc-200 rounded-[2rem] p-8 flex flex-col hover:shadow-xl transition-all duration-300">
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-2">Starter</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">$0</span>
                    <span className="text-zinc-500 font-medium">/month</span>
                  </div>
                  <p className="mt-4 text-zinc-500 text-sm">Everything you need to start sharing better AI threads.</p>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  {[
                    "Standard document layouts",
                    "Access control to manage visibility",
                    "Inline comments on content generated links"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-zinc-600" />
                      </div>
                      <span className="text-sm text-zinc-600 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={handleGetStarted}
                  className="w-full py-4 rounded-2xl border border-zinc-200 font-bold hover:bg-zinc-50 transition-colors"
                >
                  Current Plan
                </button>
              </div>

              <div className="bg-zinc-900 text-white rounded-[2rem] p-8 flex flex-col shadow-2xl shadow-blue-200 relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                <div className="absolute top-0 right-0 p-4">
                  <div className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Most Popular</div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-2">Pro</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">$3.99</span>
                    <span className="text-zinc-400 font-medium">/month</span>
                        </div>
                  <p className="mt-4 text-zinc-400 text-sm">Professional tools for power users and creators.</p>
                        </div>

                <div className="space-y-4 mb-8 flex-1">
                  {[
                    "Unlimited document creation",
                    "Analytics Dashboard"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-white" />
                        </div>
                      <span className="text-sm text-zinc-300 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={handleGetStarted}
                  className="w-full py-4 rounded-2xl bg-blue-600 font-bold hover:bg-blue-500 transition-colors flex items-center justify-center gap-2 group"
                >
                  Get Pro Access
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
                    </div>
                </section>
            </main>

      <footer className="border-t border-zinc-100 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <a href="/privacy-policy" className="text-sm font-medium text-zinc-500 hover:text-blue-600 no-underline">
            Privacy Policy
          </a>
        </div>
      </footer>
        </div>
    );
};

export default MarketingPage;
